import { Resources } from 'constants/resources';

import { useBooleanChoices } from 'hooks/useBooleanChoices';
import { useGetPlatforms } from 'pages/Merchants/hooks/useGetPlatforms';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import {
  AutocompleteInput,
  AutocompleteMultiselectInput,
  SelectInput,
  TextInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput } from 'shared/react-admin/ReferenceInputs';
import { Referral } from 'types/embedded/referral';

import { useConditionChoices } from './useConditionChoices';

export const useMerchantsListFilterFields = (): FilterFieldConfig[] => {
  const translate = useTypedTranslate();
  const platforms = useGetPlatforms();
  const conditionChoices = useConditionChoices();
  const booleanChoices = useBooleanChoices();

  return [
    {
      name: 'name',
      label: translate('app.filter.name'),
      chipType: 'text',
      input: (
        <TextInput
          helperText={false}
          id="name"
          key="name"
          label={translate('app.filter.name')}
          name="name"
          resettable
          source="name"
          variant="outlined"
        />
      ),
    },
    {
      name: 'archive',
      label: translate('app.filter.state'),
      chipType: 'text',
      chipProps: {
        getName: (filterValue: boolean) =>
          conditionChoices.find((value) => value.id === filterValue)?.name as string,
        isNotIgnoreFalseValue: true,
      },
      input: (
        <AutocompleteInput
          choices={conditionChoices}
          helperText={false}
          label={translate('app.filter.state')}
          name="archive"
          source="archive"
          variant="outlined"
        />
      ),
    },
    {
      name: 'companies',
      label: translate('app.filter.companies'),
      chipType: 'multiselect',
      chipProps: {
        resource: Resources.Companies,
        limitTags: 1,
      },
      input: (
        <ReferenceArrayInput perPage={200} reference={Resources.Companies} source="companies">
          <AutocompleteMultiselectInput
            helperText={false}
            label={translate('app.filter.companies')}
            name="companies"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'referrals',
      label: translate('app.filter.referrals'),
      chipType: 'multiselect',
      chipProps: {
        resource: Resources.Referrals,
        limitTags: 1,
      },
      input: (
        <ReferenceArrayInput perPage={200} reference={Resources.Referrals} source="referrals">
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => {
              if (!isNaN(Number(searchText))) {
                return { refId: searchText };
              }
              return { name: searchText };
            }}
            getOptionLabel={(record: Referral) => `${record?.refId}: ${record?.name}`}
            helperText={false}
            label={translate('app.filter.referrals')}
            name="referrals"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'platform',
      label: translate('app.filter.platform'),
      chipType: 'text',
      chipProps: {
        getName: (filterValue: string) =>
          platforms.find((value) => value.value === filterValue)?.label as string,
      },
      input: (
        <SelectInput
          choices={platforms}
          fullWidth
          helperText={false}
          label={translate('app.filter.platform')}
          name="platform"
          optionText="label"
          optionValue="value"
          size="small"
          source="platform"
          variant="outlined"
        />
      ),
    },
    {
      name: 'partner',
      label: translate('app.filter.partners'),
      chipType: 'text',
      chipProps: {
        getName: (filterValue: boolean) =>
          booleanChoices.find((value) => value.id === filterValue)?.name as string,
        isNotIgnoreFalseValue: true,
      },
      input: (
        <AutocompleteInput
          choices={booleanChoices}
          fullWidth
          helperText={false}
          label={translate('app.filter.partners')}
          name="partner"
          source="partner"
          variant="outlined"
        />
      ),
    },
  ];
};
